import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

// Basic debounce hook for values
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Advanced debounce hook with callback function
export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
  deps: React.DependencyList = []
): T {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const callbackRef = useRef<T>(callback);

  // Update callback ref when dependencies change
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback, ...deps]);

  // Create debounced function
  const debouncedCallback = useCallback(
    ((...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    }) as T,
    [delay]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
}

// Hook for debounced search functionality
export function useDebouncedSearch(
  searchFunction: (query: string) => Promise<any> | any,
  delay: number = 300
) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const debouncedQuery = useDebounce(query, delay);
  const searchAbortControllerRef = useRef<AbortController | null>(null);

  // Perform search when debounced query changes
  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setResults([]);
      setIsSearching(false);
      setError(null);
      return;
    }

    const performSearch = async () => {
      // Cancel previous search if still running
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
      }

      // Create new abort controller for this search
      searchAbortControllerRef.current = new AbortController();
      
      setIsSearching(true);
      setError(null);

      try {
        const searchResults = await searchFunction(debouncedQuery);
        
        // Only update results if this search wasn't aborted
        if (!searchAbortControllerRef.current.signal.aborted) {
          setResults(Array.isArray(searchResults) ? searchResults : []);
        }
      } catch (err) {
        if (!searchAbortControllerRef.current.signal.aborted) {
          console.error('Search error:', err);
          setError(err instanceof Error ? err.message : 'Search failed');
          setResults([]);
        }
      } finally {
        if (!searchAbortControllerRef.current.signal.aborted) {
          setIsSearching(false);
        }
      }
    };

    performSearch();

    // Cleanup function
    return () => {
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
      }
    };
  }, [debouncedQuery, searchFunction]);

  // Clear search
  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setError(null);
    setIsSearching(false);
    
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }
  }, []);

  return {
    query,
    setQuery,
    debouncedQuery,
    results,
    isSearching,
    error,
    clearSearch,
  };
}

// Hook for debounced form validation
export function useDebouncedValidation<T extends Record<string, any>>(
  formData: T,
  validationFunction: (data: T) => Record<keyof T, string | null>,
  delay: number = 500
) {
  const [errors, setErrors] = useState<Record<keyof T, string | null>>({} as Record<keyof T, string | null>);
  const [isValidating, setIsValidating] = useState(false);
  const [isValid, setIsValid] = useState(true);

  const debouncedFormData = useDebounce(formData, delay);

  useEffect(() => {
    const validateForm = async () => {
      setIsValidating(true);
      
      try {
        const validationErrors = await validationFunction(debouncedFormData);
        setErrors(validationErrors);
        
        // Check if form is valid (no errors)
        const hasErrors = Object.values(validationErrors).some(error => error !== null);
        setIsValid(!hasErrors);
      } catch (error) {
        console.error('Validation error:', error);
        setIsValid(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateForm();
  }, [debouncedFormData, validationFunction]);

  return {
    errors,
    isValidating,
    isValid,
    hasErrors: Object.values(errors).some(error => error !== null),
  };
}

// Hook for debounced API calls
export function useDebouncedApiCall<T, P extends any[]>(
  apiFunction: (...args: P) => Promise<T>,
  delay: number = 300
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastCallArgs, setLastCallArgs] = useState<P | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);

  const debouncedCall = useDebouncedCallback(
    async (...args: P) => {
      // Cancel previous call
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();
      
      setLoading(true);
      setError(null);
      setLastCallArgs(args);

      try {
        const result = await apiFunction(...args);
        
        if (!abortControllerRef.current.signal.aborted) {
          setData(result);
        }
      } catch (err) {
        if (!abortControllerRef.current.signal.aborted) {
          console.error('API call error:', err);
          setError(err instanceof Error ? err.message : 'API call failed');
        }
      } finally {
        if (!abortControllerRef.current.signal.aborted) {
          setLoading(false);
        }
      }
    },
    delay
  );

  // Retry last call
  const retry = useCallback(() => {
    if (lastCallArgs) {
      debouncedCall(...lastCallArgs);
    }
  }, [lastCallArgs, debouncedCall]);

  // Cancel current call
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setLoading(false);
    }
  }, []);

  // Reset state
  const reset = useCallback(() => {
    cancel();
    setData(null);
    setError(null);
    setLastCallArgs(null);
  }, [cancel]);

  return {
    data,
    loading,
    error,
    call: debouncedCall,
    retry,
    cancel,
    reset,
  };
}

// Hook for debounced auto-save functionality
export function useDebouncedAutoSave<T>(
  data: T,
  saveFunction: (data: T) => Promise<void>,
  delay: number = 2000,
  enabled: boolean = true
) {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const debouncedData = useDebounce(data, delay);
  const initialDataRef = useRef<T>(data);
  const lastSavedDataRef = useRef<T>(data);

  // Track if data has changed since last save
  useEffect(() => {
    const hasChanged = JSON.stringify(data) !== JSON.stringify(lastSavedDataRef.current);
    setIsDirty(hasChanged);
  }, [data]);

  // Auto-save when debounced data changes
  useEffect(() => {
    if (!enabled || !isDirty) return;

    const autoSave = async () => {
      setIsSaving(true);
      setSaveError(null);

      try {
        await saveFunction(debouncedData);
        setLastSaved(new Date());
        setIsDirty(false);
        lastSavedDataRef.current = debouncedData;
      } catch (error) {
        console.error('Auto-save error:', error);
        setSaveError(error instanceof Error ? error.message : 'Save failed');
      } finally {
        setIsSaving(false);
      }
    };

    autoSave();
  }, [debouncedData, saveFunction, enabled, isDirty]);

  // Manual save function
  const save = useCallback(async () => {
    if (!isDirty) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      await saveFunction(data);
      setLastSaved(new Date());
      setIsDirty(false);
      lastSavedDataRef.current = data;
    } catch (error) {
      console.error('Manual save error:', error);
      setSaveError(error instanceof Error ? error.message : 'Save failed');
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [data, saveFunction, isDirty]);

  // Reset to last saved state
  const resetToLastSaved = useCallback(() => {
    return lastSavedDataRef.current;
  }, []);

  return {
    isSaving,
    lastSaved,
    saveError,
    isDirty,
    save,
    resetToLastSaved,
    hasUnsavedChanges: isDirty,
  };
}

// Hook for debounced input with validation
export function useDebouncedInput(
  initialValue: string = '',
  validator?: (value: string) => string | null,
  delay: number = 300
) {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const debouncedValue = useDebounce(value, delay);

  const validatorRef = useRef(validator);
  
  // Update validator ref when it changes
  useEffect(() => {
    validatorRef.current = validator;
  }, [validator]);

  // Validate debounced value
  useEffect(() => {
    if (!validatorRef.current) return;

    const validateValue = async () => {
      setIsValidating(true);
      
      try {
        const validationError = await validatorRef.current(debouncedValue);
        setError(validationError);
      } catch (err) {
        setError('Validation failed');
      } finally {
        setIsValidating(false);
      }
    };

    validateValue();
  }, [debouncedValue]);

  const onChange = useCallback((newValue: string) => {
    setValue(newValue);
    // Clear error immediately when user starts typing
    if (error) {
      setError(null);
    }
  }, [error]);

  const reset = useCallback(() => {
    setValue(initialValue);
    setError(null);
  }, [initialValue]);

  return {
    value,
    debouncedValue,
    error,
    isValidating,
    isValid: !error && !isValidating,
    onChange,
    reset,
  };
}

// Utility function to create a debounced version of any function
export function createDebouncedFunction<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): T & { cancel: () => void; flush: () => void } {
  let timeoutId: NodeJS.Timeout | null = null;
  let lastArgs: Parameters<T> | null = null;

  const debouncedFunc = ((...args: Parameters<T>) => {
    lastArgs = args;
    
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func(...args);
      timeoutId = null;
    }, delay);
  }) as T & { cancel: () => void; flush: () => void };

  debouncedFunc.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  debouncedFunc.flush = () => {
    if (timeoutId && lastArgs) {
      clearTimeout(timeoutId);
      func(...lastArgs);
      timeoutId = null;
    }
  };

  return debouncedFunc;
}

export default useDebounce;